// ============================================================
// SpeedmasterCore.TemplateOverrides
//
// JS workaround for V4 fallback theme template override bug.
// (SCS 2025.2, Bundle 521562 — TemplatesCompilerMapReduce.js)
//
// Strategy: intercept bxSlider initialization so it never touches
// the CCT widget. Then transform clean DOM into scroll carousel.
//
// Two-phase approach:
//   Phase 1 (immediate): Patch jQuery.fn.bxSlider to skip elements
//           inside .item-recsys-widget-container. This runs at
//           define-time, before any view renders.
//   Phase 2 (observer):  MutationObserver detects the widget container
//           after render. Since bxSlider was blocked, the DOM is clean.
//           Transform adds .sm-scroll-track, .sm-scroll-carousel, arrows.
//
// When NetSuite fixes the template override bug, remove this module
// and use proper template override files instead.
// ============================================================

define('SpeedmasterCore.TemplateOverrides', [
	'underscore'
], function (_) {
	'use strict';

	// ----------------------------------------------------------------
	// Phase 1: Block bxSlider on CCT widgets
	//
	// Patch jQuery.fn.bxSlider ONCE at load time. The patched version
	// checks if the element is inside .item-recsys-widget-container.
	// If yes, skip (return this). If no, call original bxSlider.
	// ----------------------------------------------------------------

	(function patchBxSlider() {
		if (typeof jQuery === 'undefined') { return; }

		var _waitAttempts = 0;
		var _maxAttempts = 50; // 50 × 100ms = 5 seconds

		function tryPatch() {
			if (jQuery.fn.bxSlider) {
				var _originalBxSlider = jQuery.fn.bxSlider;

				// Mock bxSlider instance — CCT widget stores this as this.slider
				// and calls this.slider.destroySlider() on navigation
				var _mockSlider = {
					destroySlider: function () {},
					reloadSlider: function () {},
					getCurrentSlide: function () { return 0; },
					getSlideCount: function () { return 0; },
					goToSlide: function () {},
					goToNextSlide: function () {},
					goToPrevSlide: function () {}
				};

				jQuery.fn.bxSlider = function bxSliderPatched(options) {
					// Filter out elements inside CCT widget containers
					var filtered = this.filter(function () {
						return !jQuery(this).closest('.item-recsys-widget-container').length;
					});

					// Call original bxSlider only on non-CCT elements
					if (filtered.length) {
						return _originalBxSlider.call(filtered, options);
					}

					// Return mock slider so widget.destroySlider() doesn't crash
					return _mockSlider;
				};
				return; // patched successfully
			}

			// bxSlider plugin not loaded yet — retry
			_waitAttempts++;
			if (_waitAttempts < _maxAttempts) {
				setTimeout(tryPatch, 100);
			}
		}

		// Start immediately — bxSlider may already be loaded or load soon
		tryPatch();
	})();

	// ----------------------------------------------------------------
	// SVG arrow markup — V4 icon rules (24x24 viewBox, 16x16, stroke 2.5)
	// ----------------------------------------------------------------

	var ARROW_PREV = '<button class="sm-scroll-arrow sm-scroll-prev" aria-label="Previous">'
		+ '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"'
		+ ' stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">'
		+ '<polyline points="15 18 9 12 15 6"/></svg></button>';

	var ARROW_NEXT = '<button class="sm-scroll-arrow sm-scroll-next" aria-label="Next">'
		+ '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"'
		+ ' stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">'
		+ '<polyline points="9 6 15 12 9 18"/></svg></button>';

	// ----------------------------------------------------------------
	// Helper: create element from HTML string
	// ----------------------------------------------------------------

	function htmlToElement(html) {
		var temp = document.createElement('div');
		temp.innerHTML = html;
		return temp.firstChild;
	}

	// ----------------------------------------------------------------
	// Phase 2: Transform CCT widget DOM → scroll carousel
	//
	// Since bxSlider is blocked (Phase 1), the DOM is clean:
	//   <aside class="item-recsys-widget-container ...">
	//     <h3>Header</h3>
	//     <div class="item-relations-related-row">
	//       <div>
	//         <ul class="item-relations-row" data-type="carousel-items">
	//           <li>...</li>
	//
	// We:
	//   1. Remove data-type="carousel-items" (safety — prevent any future init)
	//   2. Add .sm-scroll-track to <ul>
	//   3. Add .sm-scroll-carousel to row div + inject arrows
	//
	// If bxSlider DID somehow init (patch timing edge case), clean up.
	// ----------------------------------------------------------------

	function cleanupBxSlider(container) {
		var bxWrapper = container.querySelector('.bx-wrapper');
		if (!bxWrapper) { return; }

		// Try jQuery API first
		if (typeof jQuery !== 'undefined') {
			var $ul = jQuery(container).find('ul.item-relations-row');
			if ($ul.length && $ul.data('bxSlider')) {
				try { $ul.data('bxSlider').destroySlider(); } catch (e) {}
				return;
			}
		}

		// Manual cleanup
		var ul = bxWrapper.querySelector('ul.item-relations-row');
		if (!ul) { return; }

		var clones = ul.querySelectorAll('.bx-clone');
		for (var c = 0; c < clones.length; c++) {
			clones[c].parentNode.removeChild(clones[c]);
		}
		ul.removeAttribute('style');

		var items = ul.querySelectorAll('li');
		for (var i = 0; i < items.length; i++) {
			items[i].removeAttribute('style');
			items[i].removeAttribute('aria-hidden');
		}

		var controls = bxWrapper.querySelector('.bx-controls');
		if (controls) { controls.parentNode.removeChild(controls); }

		var rowDiv = bxWrapper.parentNode;
		if (rowDiv) {
			rowDiv.insertBefore(ul, bxWrapper);
			rowDiv.removeChild(bxWrapper);
		}
	}

	function transformItemRecSysWidget(container) {
		if (container.getAttribute('data-sm-tpl-override')) { return; }
		container.setAttribute('data-sm-tpl-override', 'true');

		// Safety: clean up bxSlider if it somehow got through
		cleanupBxSlider(container);

		// Find the <ul>
		var ul = container.querySelector('ul.item-relations-row');
		if (!ul) { return; }

		// Remove bxSlider trigger
		ul.removeAttribute('data-type');

		// Add scroll track
		ul.classList.add('sm-scroll-track');

		// Strip any inline styles (bxSlider or SC default)
		var styledEls = ul.querySelectorAll('[style]');
		for (var s = 0; s < styledEls.length; s++) {
			styledEls[s].removeAttribute('style');
		}

		// Add scroll carousel wrapper + arrows
		var rowDiv = container.querySelector('.item-relations-related-row');
		if (rowDiv) {
			rowDiv.classList.add('sm-scroll-carousel');
			rowDiv.insertBefore(htmlToElement(ARROW_PREV), rowDiv.firstChild);
			rowDiv.appendChild(htmlToElement(ARROW_NEXT));
		}

		// ScrollCarousel.js MutationObserver auto-detects .sm-scroll-carousel
	}

	// ----------------------------------------------------------------
	// MutationObserver — detect extension DOM for transforms
	// ----------------------------------------------------------------

	// ----------------------------------------------------------------
	// Blog "Read More" → descriptive link text (SEO)
	//
	// Lighthouse flags generic "Read More" links. The Blog extension
	// template (V4 override bug) can't be replaced, so we fix in DOM.
	// Pull the post title from the card and rewrite the link text.
	// The link is display:none but Lighthouse still reads it.
	// ----------------------------------------------------------------

	function fixBlogReadMoreLinks(card) {
		if (card.getAttribute('data-sm-tpl-override')) { return; }
		card.setAttribute('data-sm-tpl-override', 'true');

		var titleEl = card.querySelector('[class*="-title"] a, [class*="-title"]');
		var title = titleEl ? titleEl.textContent.trim() : '';
		var readMoreLink = card.querySelector('[class*="-read-more-action"] a');

		if (readMoreLink) {
			if (title) {
				readMoreLink.textContent = 'Read more about ' + title;
				readMoreLink.setAttribute('aria-label', 'Read more about ' + title);
			} else {
				// No title found — remove the link entirely
				var wrapper = readMoreLink.closest('[class*="-read-more-action"]');
				if (wrapper) { wrapper.parentNode.removeChild(wrapper); }
			}
		}
	}

	var transforms = [
		{
			selector: '.item-recsys-widget-container:not([data-sm-tpl-override])',
			fn: transformItemRecSysWidget
		},
		{
			selector: '.sc-blog-post-card-vertical:not([data-sm-tpl-override]), .sc-blog-post-card-horizontal:not([data-sm-tpl-override])',
			fn: fixBlogReadMoreLinks
		}
	];

	function scanForTransforms() {
		for (var t = 0; t < transforms.length; t++) {
			var els = document.querySelectorAll(transforms[t].selector);
			for (var i = 0; i < els.length; i++) {
				transforms[t].fn(els[i]);
			}
		}
	}

	var observer = new MutationObserver(function () {
		scanForTransforms();
	});

	if (document.body) {
		observer.observe(document.body, { childList: true, subtree: true });
		scanForTransforms();
	} else {
		document.addEventListener('DOMContentLoaded', function () {
			observer.observe(document.body, { childList: true, subtree: true });
			scanForTransforms();
		});
	}
});
